import { inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { CanActivateFn, Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { filter } from 'rxjs/operators';
import { AuthService, PermissionService } from '@zag/auth/ui';

/**
 * Auth guard for the control UI.
 *
 * Checks whether the user has an active session and permission state.
 * Redirects unauthenticated users to `/login` with a returnUrl.
 * Redirects users with no permissions to `/auth/pending` with a returnUrl.
 * Redirects users with permission loading errors to `/auth/error`.
 */
export const controlAuthGuard: CanActivateFn = async (_route, state) => {
  const authService = inject(AuthService);
  const permissionService = inject(PermissionService);
  const router = inject(Router);

  if (!authService.isAuthenticated()) {
    return router.createUrlTree(['/login'], {
      queryParams: { returnUrl: state.url },
    });
  }

  if (!permissionService.isLoaded()) {
    await firstValueFrom(toObservable(permissionService.isLoaded).pipe(filter(loaded => loaded)));
  }

  if (permissionService.hasError()) {
    return router.createUrlTree(['/auth/error'], {
      queryParams: { returnUrl: state.url },
    });
  }

  if (permissionService.hasNoPermissions()) {
    return router.createUrlTree(['/auth/pending'], {
      queryParams: { returnUrl: state.url },
    });
  }

  return true;
};
